import { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { color, Icon, typography } from '@uniquegood/realworld-studio-design';
import { IFAQItem } from '../../types';
import { getAccessToken } from '@/auth';
import { ClickEventName, track } from '@/track';

// 링크 매핑 정의
const LINK_MAP: Record<string, string> = {
  '환불 정책': 'https://uniquegoodcompany.notion.site/29a18b2eed608046b572d54167c45d3e',
  '리얼월드 스쿨 웹사이트': 'https://realworld-school.org/theme/realworld/subscribe'
};

// 텍스트에서 {텍스트} 패턴을 찾아 링크로 변환하는 함수
const parseAnswerWithLinks = (text: string): (string | JSX.Element)[] => {
  const parts: (string | JSX.Element)[] = [];
  const regex = /\{([^}]+)\}/g;
  let lastIndex = 0;
  let match = regex.exec(text);
  let keyCounter = 0;

  while (match !== null) {
    // 링크 이전의 텍스트 추가
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    const linkText = match[1];
    const linkUrl = LINK_MAP[linkText];

    if (linkUrl) {
      // 링크가 매핑되어 있으면 링크로 변환
      const currentKey = keyCounter;
      keyCounter += 1;
      parts.push(
        <AnswerLink
          key={`link-${currentKey}`}
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {linkText}
        </AnswerLink>
      );
    } else {
      // 링크가 없으면 그냥 텍스트로 표시 (중괄호 제거)
      parts.push(linkText);
    }

    lastIndex = regex.lastIndex;
    match = regex.exec(text);
  }

  // 마지막 부분 추가
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
};

interface FAQSectionProps {
  title: string;
  items: IFAQItem[];
}

const FAQSection = ({ title, items }: FAQSectionProps) => {
  const [openItemId, setOpenItemId] = useState<string | null>(null);

  // ChannelIO 초기화 (pricing 페이지는 public 페이지이므로 사용자 정보 없이 초기화)
  useEffect(() => {
    if (ChannelIO) {
      ChannelIO('boot', {
        pluginKey: 'd7a0ab75-47ba-410e-b6b8-7cc506a4d702',
        profile: {
          source: '스튜디오'
        },
        hideChannelButtonOnBoot: true
      });
    }
  }, []);

  const toggleItem = async (id: string) => {
    const accessToken = await getAccessToken();
    if (openItemId !== id) {
      track.onClick({
        clickEventName: ClickEventName.click_landing_plan_faq_expand,
        params: { is_logged_in: accessToken ? 'true' : 'false', faq_number: id }
      });
    }
    setOpenItemId(openItemId === id ? null : id);
  };

  const handleContactClick = async () => {
    const accessToken = await getAccessToken();
    track.onClick({
      clickEventName: ClickEventName.click_landing_plan_faq_contact,
      params: { is_logged_in: accessToken ? 'true' : 'false' }
    });

    if (ChannelIO) {
      ChannelIO('showMessenger');
    }
  };

  return (
    <SectionContainer>
      <TwoColumnLayout>
        <LeftColumn>
          <HeaderWrapper>
            <SectionTitle>{title}</SectionTitle>
            <Description>
              궁금한 점 또는 필요한 내용을 확인해보세요.
              <br />좀 더 상세한 안내를 원하신다면 1:1 문의하기를 이용해 주세요.
            </Description>
          </HeaderWrapper>

          <ContactCard>
            <ContactHeader>
              <LogoWrapper>
                <LogoIcon>R!</LogoIcon>
              </LogoWrapper>
              <ContactInfo>
                <ContactTitle>1:1 문의하기</ContactTitle>
                <ContactSubtitle>원하는 답변이 없다면 리얼월드 팀에 문의해 주세요.</ContactSubtitle>
              </ContactInfo>
            </ContactHeader>
            <ContactButton onClick={handleContactClick}>문의하기</ContactButton>
          </ContactCard>
        </LeftColumn>

        <RightColumn>
          <FAQList>
            {items.map((item) => (
              <FAQItem key={item.id} isOpen={openItemId === item.id}>
                <QuestionButton onClick={() => toggleItem(item.id)} type="button">
                  <ChevronIcon isOpen={openItemId === item.id}>
                    <Icon icon="chevron_right_regular" size="16px" />
                  </ChevronIcon>
                  <Question>{item.question}</Question>
                </QuestionButton>
                {openItemId === item.id && item.answer && (
                  <Answer>{parseAnswerWithLinks(item.answer)}</Answer>
                )}
              </FAQItem>
            ))}
          </FAQList>
        </RightColumn>
      </TwoColumnLayout>
    </SectionContainer>
  );
};

export default FAQSection;

const SectionContainer = styled.section`
  width: 100%;
  background: #f5f5f5;
  padding: 64px 24px;

  @media (max-width: 768px) {
    padding: 48px 16px;
  }
`;

const TwoColumnLayout = styled.div`
  max-width: 920px;
  margin: 0 auto;
  display: flex;
  gap: 84px;

  @media (max-width: 968px) {
    flex-direction: column;
  }
`;

const LeftColumn = styled.div`
  flex: 0 0 376px;
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (max-width: 968px) {
    flex: 1;
  }
`;

const RightColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionTitle = styled.h2`
  font-size: 28px;
  line-height: 32px;
  font-weight: ${typography.weight.bold};
  color: ${color.text_default};
  margin: 0;
`;

const Description = styled.p`
  font-size: 14px;
  line-height: 20px;
  font-weight: ${typography.weight.regular};
  color: #101010;
  margin: 0;
`;

const ContactCard = styled.div`
  background: ${color.white};
  border-radius: 16px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.12);
`;

const ContactHeader = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

const LogoWrapper = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 8px;
  background: ${color.white};
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const LogoIcon = styled.div`
  font-size: 28px;
  font-weight: ${typography.weight.bold};
  background: linear-gradient(135deg, #7800ff 0%, #9d4edd 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ContactTitle = styled.div`
  font-size: 16px;
  line-height: 24px;
  font-weight: ${typography.weight.bold};
  color: #101010;
`;

const ContactSubtitle = styled.div`
  font-size: 12px;
  line-height: 16px;
  font-weight: ${typography.weight.regular};
  color: #101010;
`;

const ContactButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 20px;
  font-weight: ${typography.weight.medium};
  background: #7800ff;
  color: ${color.white};
  border: none;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FAQItem = styled.div<{ isOpen: boolean }>`
  background: #fafafa;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.2s;
`;

const QuestionButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`;

const ChevronIcon = styled.span<{ isOpen: boolean }>`
  width: 16px;
  height: 16px;
  font-size: 16px;
  font-weight: ${typography.weight.bold};
  color: ${color.text_default};
  transition: transform 0.2s;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(90deg)' : 'rotate(0)')};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Question = styled.div`
  font-size: 16px;
  line-height: 24px;
  font-weight: ${typography.weight.bold};
  color: #101010;
  flex: 1;
`;

const Answer = styled.div`
  padding: 0 24px 24px 48px;
  font-size: 14px;
  line-height: 20px;
  font-weight: ${typography.weight.regular};
  color: #101010;
  white-space: pre-line;
  animation: fadeIn 0.3s ease-in;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const AnswerLink = styled.a`
  color: ${color.text_default};
  text-decoration: underline;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;
